// src/contexts/DashboardContext.js

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import DashboardService from '../services/DashboardService';

const DashboardContext = createContext();

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
};

export const DashboardProvider = ({ children }) => {
  const { currentUser, currentCompany } = useAuth();
  
  // Dashboard metrics state
  const [metrics, setMetrics] = useState({
    departments: 0,
    staffMembers: 0,
    activeSessions: 0
  });
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [metricsError, setMetricsError] = useState(null);

  // Active staff state
  const [activeStaff, setActiveStaff] = useState([]);
  const [activeStaffLoading, setActiveStaffLoading] = useState(true);
  const [activeStaffError, setActiveStaffError] = useState(null);

  // Recent activity state
  const [recentActivity, setRecentActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [activityError, setActivityError] = useState(null);
  const [activityHasMore, setActivityHasMore] = useState(true);

  // Dashboard alerts state
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [alertsError, setAlertsError] = useState(null);

  // Departments state - Keep minimal for dashboard filtering only
  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(true);
  const [departmentsError, setDepartmentsError] = useState(null);

  // Admin operations state
  const [adminOperationLoading, setAdminOperationLoading] = useState(false);
  const [adminOperationError, setAdminOperationError] = useState(null);

  // Time management state
  const [timeEntries, setTimeEntries] = useState({});
  const [timeEntriesLoading, setTimeEntriesLoading] = useState(false);
  const [timeEntriesError, setTimeEntriesError] = useState(null);
  const [activeSessionDetail, setActiveSessionDetail] = useState(null);
  const [activeSessionDetailLoading, setActiveSessionDetailLoading] = useState(false);

  // Hours balance outliers state (Hours Account multi-location visibility)
  // On-demand/read-only only — deliberately NOT part of refreshDashboard()'s
  // Promise.all and NOT wired into setupSubscriptions(). This data only
  // changes via the nightly Cloud Function (hours-account-nightly-recalc)
  // or a manual opening-balance/adjustment edit, so it is fetched on the
  // widget's mount (and on manual refresh) only — same on-demand shape as
  // getActiveSessionDetail below.
  const [hoursBalanceOutliers, setHoursBalanceOutliers] = useState(null);
  const [hoursBalanceOutliersLoading, setHoursBalanceOutliersLoading] = useState(false);
  const [hoursBalanceOutliersError, setHoursBalanceOutliersError] = useState(null);

  // Filter states
  const [activeTab, setActiveTab] = useState('active'); // 'active', 'activity', 'alerts'
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [searchFilter, setSearchFilter] = useState('');

  // Subscription references
  const [subscriptions, setSubscriptions] = useState([]);

  const companyId = currentCompany?.id;

  /**
   * Load dashboard metrics
   */
  const loadDashboardMetrics = useCallback(async () => {
    if (!companyId) return;

    try {
      setMetricsLoading(true);
      setMetricsError(null);
      const metricsData = await DashboardService.getDashboardMetrics(companyId);
      setMetrics(metricsData);
    } catch (error) {
      console.error('DashboardContext: Error loading dashboard metrics:', error);
      setMetricsError('Failed to load dashboard metrics');
    } finally {
      setMetricsLoading(false);
    }
  }, [companyId]);

  /**
   * Load currently active staff
   */
  const loadActiveStaff = useCallback(async () => {
    if (!companyId) return;

    try {
      setActiveStaffLoading(true);
      setActiveStaffError(null);
      const activeStaffData = await DashboardService.getCurrentlyActiveStaff(companyId);
      setActiveStaff(activeStaffData);
    } catch (error) {
      console.error('DashboardContext: Error loading active staff:', error);
      setActiveStaffError('Failed to load active staff');
    } finally {
      setActiveStaffLoading(false);
    }
  }, [companyId]);

  /**
   * Load recent activity
   *
   * BUGFIX: DashboardService.getRecentActivity() now re-throws instead of
   * silently returning [] on failure (see DashboardService.js). This catch
   * block already correctly sets activityError — previously it simply never
   * ran for this failure mode because the error never reached here. Now
   * that it does, activityError will be correctly surfaced to ActivityFeed,
   * and the activeTab-triggered retry effect below can attempt recovery
   * automatically without requiring a full page reload.
   */
  const loadRecentActivity = useCallback(async (reset = false) => {
    if (!companyId) return;

    try {
      if (reset) {
        setActivityLoading(true);
        setActivityError(null);
      }

      const activityData = await DashboardService.getRecentActivity(companyId, 25);
      
      if (reset) {
        setRecentActivity(activityData);
      } else {
        setRecentActivity(prev => [...prev, ...activityData]);
      }
      
      setActivityHasMore(activityData.length === 25);
    } catch (error) {
      console.error('DashboardContext: Error loading recent activity:', error);
      setActivityError('Failed to load recent activity');
    } finally {
      setActivityLoading(false);
    }
  }, [companyId]);

  /**
   * Load dashboard alerts
   */
  const loadDashboardAlerts = useCallback(async () => {
    if (!companyId) return;

    try {
      setAlertsLoading(true);
      setAlertsError(null);
      const alertsData = await DashboardService.getDashboardAlerts(companyId);
      setAlerts(alertsData);
    } catch (error) {
      console.error('DashboardContext: Error loading dashboard alerts:', error);
      setAlertsError('Failed to load alerts');
    } finally {
      setAlertsLoading(false);
    }
  }, [companyId]);

  /**
   * Load departments - Lightweight version for dashboard filtering only
   */
  const loadDepartments = useCallback(async () => {
    if (!companyId) {
      setDepartments([]);
      setDepartmentsLoading(false);
      return;
    }

    try {
      setDepartmentsLoading(true);
      setDepartmentsError(null);
      
      console.log('DashboardContext: Loading departments for filtering...');
      const departmentsData = await DashboardService.getDepartments(companyId);
      
      // Only store essential department info needed for dashboard filtering
      const filteredDepartments = departmentsData.map(dept => ({
        id: dept.id,
        name: dept.name || `Department ${dept.id.substring(0, 8)}`
      }));
      
      console.log('DashboardContext: Departments loaded for filtering:', filteredDepartments.length);
      setDepartments(filteredDepartments);
    } catch (error) {
      console.error('DashboardContext: Error loading departments:', error);
      setDepartmentsError('Failed to load departments for filtering');
      setDepartments([]);
    } finally {
      setDepartmentsLoading(false);
    }
  }, [companyId]);

  /**
   * Get active session details for a specific staff member
   */
  const getActiveSessionDetail = useCallback(async (staffId) => {
    if (!companyId || !staffId) return null;

    try {
      setActiveSessionDetailLoading(true);
      const sessionDetail = await DashboardService.getActiveSessionDetail(companyId, staffId);
      setActiveSessionDetail(sessionDetail);
      return sessionDetail;
    } catch (error) {
      console.error('DashboardContext: Error loading active session detail:', error);
      setActiveSessionDetail(null);
      throw error;
    } finally {
      setActiveSessionDetailLoading(false);
    }
  }, [companyId]);

  /**
   * Load Hours Account balance outliers (largest positive/negative YTD
   * balances across all staff/departments — multi-location visibility).
   * Read-only: reads the nightly-computed ytdBalance field only, never
   * calculates or writes. On-demand only — call on widget mount and on
   * manual refresh; not part of refreshDashboard()/setupSubscriptions().
   */
  const loadHoursBalanceOutliers = useCallback(async (options = {}) => {
    if (!companyId) return null;

    try {
      setHoursBalanceOutliersLoading(true);
      setHoursBalanceOutliersError(null);
      const outliersData = await DashboardService.getHoursBalanceOutliers(companyId, options);
      setHoursBalanceOutliers(outliersData);
      return outliersData;
    } catch (error) {
      console.error('DashboardContext: Error loading hours balance outliers:', error);
      setHoursBalanceOutliersError('Failed to load hours balance outliers');
      throw error;
    } finally {
      setHoursBalanceOutliersLoading(false);
    }
  }, [companyId]);

  /**
   * Load time entries for a specific staff member
   */
  const loadTimeEntriesForStaff = useCallback(async (staffId, dateRange = null) => {
    if (!companyId || !staffId) return;

    try {
      setTimeEntriesLoading(true);
      setTimeEntriesError(null);
      
      const entries = await DashboardService.getTimeEntriesForStaff(companyId, staffId, dateRange);
      
      setTimeEntries(prev => ({
        ...prev,
        [staffId]: entries
      }));
      
      return entries;
    } catch (error) {
      console.error('DashboardContext: Error loading time entries:', error);
      setTimeEntriesError('Failed to load time entries');
      throw error;
    } finally {
      setTimeEntriesLoading(false);
    }
  }, [companyId]);

  /**
   * Update a time entry with new timestamps
   */
  const updateTimeEntry = useCallback(async (staffId, entryId, newTimestamps) => {
    if (!companyId || !staffId || !entryId) {
      throw new Error('Company ID, Staff ID, and Entry ID are required');
    }

    try {
      setAdminOperationLoading(true);
      setAdminOperationError(null);

      await DashboardService.updateTimeEntry(companyId, staffId, entryId, newTimestamps);
      
      // Refresh time entries for this staff member
      await loadTimeEntriesForStaff(staffId);
      
      // Refresh dashboard data
      await refreshDashboard();
      
      console.log('DashboardContext: Time entry updated successfully');
    } catch (error) {
      console.error('DashboardContext: Error updating time entry:', error);
      setAdminOperationError('Failed to update time entry');
      throw error;
    } finally {
      setAdminOperationLoading(false);
    }
  }, [companyId, loadTimeEntriesForStaff]);

  /**
   * Refresh all dashboard data
   */
  const refreshDashboard = useCallback(async () => {
    console.log('DashboardContext: Refreshing all dashboard data...');
    
    try {
      await Promise.all([
        loadDashboardMetrics(),
        loadActiveStaff(),
        loadRecentActivity(true),
        loadDashboardAlerts(),
        loadDepartments()
      ]);
      
      console.log('DashboardContext: Dashboard refresh completed');
    } catch (error) {
      console.error('DashboardContext: Error refreshing dashboard:', error);
    }
  }, [loadDashboardMetrics, loadActiveStaff, loadRecentActivity, loadDashboardAlerts, loadDepartments]);

  /**
   * Admin clock out staff member
   *
   * IMPORTANT: Do NOT call refreshDashboard() here. activeStaff / metrics /
   * alerts / recentActivity all update automatically via the real-time
   * subscriptions set up in setupSubscriptions() (subscribeToActiveStaff,
   * subscribeToDashboardMetrics, subscribeToRecentActivity), which already
   * listen on the `activeSessions` collection that this operation writes to.
   *
   * Calling refreshDashboard() here toggles activeStaffLoading true -> false,
   * which causes ActiveSessionsWidget's loading early-return to unmount the
   * confirmation modal mid-flight — this was the root cause of the modal not
   * closing / reappearing after a successful clock-out.
   */
  const adminClockOutStaff = useCallback(async (staffId, adjustments = {}) => {
    if (!companyId || !staffId) {
      throw new Error('Company ID and Staff ID are required');
    }

    try {
      console.log('DashboardContext: Admin clocking out staff:', staffId);
      setAdminOperationLoading(true);
      setAdminOperationError(null);

      await DashboardService.adminClockOutStaff(companyId, staffId, adjustments);

      // Refresh the time entries cache for this staff member. Fire-and-forget
      // (not awaited) so a failure here (e.g. staff detail tab not currently
      // mounted) can NEVER block the caller from treating the clock-out as
      // successful and closing its modal.
      loadTimeEntriesForStaff(staffId).catch((err) => {
        console.error('DashboardContext: Failed to refresh time entries after admin clock out:', err);
      });

      console.log('DashboardContext: Admin clock out completed successfully');
    } catch (error) {
      console.error('DashboardContext: Error in admin clock out:', error);
      setAdminOperationError('Failed to clock out staff member');
      throw error;
    } finally {
      setAdminOperationLoading(false);
    }
  }, [companyId, loadTimeEntriesForStaff]);

  /**
   * Set up real-time subscriptions
   */
  const setupSubscriptions = useCallback(() => {
    if (!companyId) return;

    const subs = [];

    try {
      // Subscribe to dashboard metrics
      const metricsUnsub = DashboardService.subscribeToDashboardMetrics(
        companyId,
        (metricsData) => {
          setMetrics(metricsData);
          setMetricsError(null);
        }
      );
      if (metricsUnsub) subs.push(metricsUnsub);

      // Subscribe to active staff
      const activeStaffUnsub = DashboardService.subscribeToActiveStaff(
        companyId,
        (activeStaffData) => {
          setActiveStaff(activeStaffData);
          setActiveStaffError(null);
          
          // Update alerts when active staff changes
          DashboardService.getDashboardAlerts(companyId)
            .then(alertsData => {
              setAlerts(alertsData);
              setAlertsError(null);
            })
            .catch(error => {
              console.error('DashboardContext: Error updating alerts:', error);
            });
        }
      );
      if (activeStaffUnsub) subs.push(activeStaffUnsub);

      // Subscribe to recent activity
      const recentActivityUnsub = DashboardService.subscribeToRecentActivity(
        companyId,
        (activityData) => {
          setRecentActivity(activityData);
          setActivityError(null);
        }
      );
      if (recentActivityUnsub) subs.push(recentActivityUnsub);

      setSubscriptions(subs);

      return () => {
        subs.forEach(unsub => {
          if (typeof unsub === 'function') {
            try {
              unsub();
            } catch (error) {
              console.error('DashboardContext: Error unsubscribing:', error);
            }
          }
        });
      };
    } catch (error) {
      console.error('DashboardContext: Error setting up subscriptions:', error);
      return () => {};
    }
  }, [companyId]);

  /**
   * Filter active staff by department and search
   */
  const filteredActiveStaff = useCallback(() => {
    let filtered = activeStaff;

    // Filter by department
    if (departmentFilter !== 'all') {
      filtered = filtered.filter(staff => staff.departmentId === departmentFilter);
    }

    // Filter by search term
    if (searchFilter.trim()) {
      const searchTerm = searchFilter.toLowerCase().trim();
      filtered = filtered.filter(staff => 
        staff.staffName?.toLowerCase().includes(searchTerm) ||
        staff.departmentName?.toLowerCase().includes(searchTerm)
      );
    }

    return filtered;
  }, [activeStaff, departmentFilter, searchFilter]);

  /**
   * Filter recent activity by department and search
   */
  const filteredRecentActivity = useCallback(() => {
    let filtered = recentActivity;

    // Filter by department
    if (departmentFilter !== 'all') {
      filtered = filtered.filter(activity => activity.departmentId === departmentFilter);
    }

    // Filter by search term
    if (searchFilter.trim()) {
      const searchTerm = searchFilter.toLowerCase().trim();
      filtered = filtered.filter(activity => 
        activity.staffName?.toLowerCase().includes(searchTerm) ||
        activity.departmentName?.toLowerCase().includes(searchTerm)
      );
    }

    return filtered;
  }, [recentActivity, departmentFilter, searchFilter]);

  /**
   * Get alerts by severity
   */
  const getAlertsBySeverity = useCallback((severity) => {
    return alerts.filter(alert => alert.severity === severity);
  }, [alerts]);

  /**
   * Get active staff statistics
   */
  const getActiveStaffStats = useCallback(() => {
    const longSessions = activeStaff.filter(staff => staff.isLongSession).length;
    const onBreak = activeStaff.filter(staff => staff.onBreak).length;
    const working = activeStaff.length - onBreak;

    return {
      total: activeStaff.length,
      working,
      onBreak,
      longSessions
    };
  }, [activeStaff]);

  /**
   * Clear filters
   */
  const clearFilters = useCallback(() => {
    setDepartmentFilter('all');
    setSearchFilter('');
  }, []);

  /**
   * Get department name by ID - Dashboard specific version
   */
  const getDashboardDepartmentName = useCallback((departmentId) => {
    if (!departmentId) return 'Unassigned';
    
    const dept = departments.find(d => d.id === departmentId);
    return dept ? dept.name : `Department ${departmentId.substring(0, 8)}`;
  }, [departments]);

  /**
   * Clear admin operation error
   */
  const clearAdminOperationError = useCallback(() => {
    setAdminOperationError(null);
  }, []);

  /**
   * Clear time entries error
   */
  const clearTimeEntriesError = useCallback(() => {
    setTimeEntriesError(null);
  }, []);

  /**
   * Get time entries for a specific staff member
   */
  const getTimeEntriesForStaff = useCallback((staffId) => {
    return timeEntries[staffId] || [];
  }, [timeEntries]);

  /**
   * Initialize dashboard on mount
   */
  useEffect(() => {
    if (companyId) {
      console.log('DashboardContext: Initializing dashboard for company:', companyId);
      
      // Initial data load
      refreshDashboard();
      
      // Set up real-time subscriptions
      const cleanup = setupSubscriptions();
      
      return cleanup;
    } else {
      // Reset state when no company
      setMetrics({ departments: 0, staffMembers: 0, activeSessions: 0 });
      setActiveStaff([]);
      setRecentActivity([]);
      setAlerts([]);
      setDepartments([]);
      setTimeEntries({});
      setActiveSessionDetail(null);
      setHoursBalanceOutliers(null);
      setHoursBalanceOutliersLoading(false);
      setHoursBalanceOutliersError(null);
      setMetricsLoading(false);
      setActiveStaffLoading(false);
      setActivityLoading(false);
      setAlertsLoading(false);
      setDepartmentsLoading(false);
      setTimeEntriesLoading(false);
      setActiveSessionDetailLoading(false);
      setAdminOperationLoading(false);
      setAdminOperationError(null);
      setTimeEntriesError(null);
    }
  }, [companyId, refreshDashboard, setupSubscriptions]);

  /**
   * BUGFIX (Recent Activity tab stuck empty until hard refresh):
   *
   * activeTab was tracked in this context but nothing ever used it to
   * trigger a data load — the "Recent Activity" tab relied entirely on
   * the one-shot mount-time loadRecentActivity(true) call plus the passive
   * subscribeToRecentActivity listener (which only re-fires when an
   * `activeSessions` document changes). If that one-shot call silently
   * failed or raced with auth/company resolution, recentActivity stayed
   * empty for the rest of the session with no way to recover short of a
   * full page reload — clicking the tab did nothing to retry.
   *
   * This effect makes the tab self-healing: whenever the admin switches to
   * the "activity" tab and we currently have nothing to show (no data, not
   * already loading, and no error already being displayed for the user to
   * act on via the Retry button), we attempt a fresh load. This is a safety
   * net only — it intentionally does NOT fire on every tab switch when data
   * or an error already exist, so it won't cause redundant fetches or fight
   * with the real-time subscription during normal operation.
   */
  useEffect(() => {
    if (
      activeTab === 'activity' &&
      companyId &&
      !activityLoading &&
      !activityError &&
      recentActivity.length === 0
    ) {
      console.log('DashboardContext: Recent Activity tab opened with no data — retrying load...');
      loadRecentActivity(true);
    }
  }, [activeTab, companyId, activityLoading, activityError, recentActivity.length, loadRecentActivity]);

  /**
   * Cleanup subscriptions on unmount
   */
  useEffect(() => {
    return () => {
      console.log('DashboardContext: Cleaning up subscriptions...');
      
      subscriptions.forEach(unsub => {
        if (typeof unsub === 'function') {
          try {
            unsub();
          } catch (error) {
            console.error('DashboardContext: Error during cleanup:', error);
          }
        }
      });
      
      // Cleanup dashboard service
      try {
        DashboardService.unsubscribeAll();
      } catch (error) {
        console.error('DashboardContext: Error cleaning up DashboardService:', error);
      }
    };
  }, [subscriptions]);

  const contextValue = {
    // Data
    metrics,
    activeStaff,
    recentActivity,
    alerts,
    departments, // Only basic department info for filtering
    timeEntries,
    activeSessionDetail,
    hoursBalanceOutliers,

    // Loading states
    metricsLoading,
    activeStaffLoading,
    activityLoading,
    alertsLoading,
    departmentsLoading,
    adminOperationLoading,
    timeEntriesLoading,
    activeSessionDetailLoading,
    hoursBalanceOutliersLoading,

    // Error states
    metricsError,
    activeStaffError,
    activityError,
    alertsError,
    departmentsError,
    adminOperationError,
    timeEntriesError,
    hoursBalanceOutliersError,

    // UI state
    activeTab,
    departmentFilter,
    searchFilter,
    activityHasMore,

    // Filtered data
    filteredActiveStaff: filteredActiveStaff(),
    filteredRecentActivity: filteredRecentActivity(),

    // Actions
    setActiveTab,
    setDepartmentFilter,
    setSearchFilter,
    refreshDashboard,
    loadRecentActivity,
    clearFilters,
    adminClockOutStaff,
    clearAdminOperationError,
    clearTimeEntriesError,
    getActiveSessionDetail,
    loadTimeEntriesForStaff,
    updateTimeEntry,
    getTimeEntriesForStaff,
    loadHoursBalanceOutliers,

    // Computed data
    getAlertsBySeverity,
    getActiveStaffStats,
    getDashboardDepartmentName,

    // Utilities
    formatDuration: DashboardService.formatDuration,
    formatTime: DashboardService.formatTime
  };

  return (
    <DashboardContext.Provider value={contextValue}>
      {children}
    </DashboardContext.Provider>
  );
};

export default DashboardContext;